import type { ConceitoComoUtilizar, PassoComoUtilizar } from "@/components/consignado/Cobranca/VinculosAtivosRedesign/ComoUtilizarModal";

// Conteúdo dos modais de onboarding — reaproveita o mesmo ComoUtilizarModal
// já usado em Vínculos Ativos/Encerrados (Cobrança). Um conjunto de passos
// por escopo de tela (listagem, novo disparo, editar, visualizar), pra cada
// modal explicar só o que é relevante ali. O glossário é compartilhado.
//
// Os termos seguem o cadastro real (HIS-APP-notificacoes-007): contexto,
// gatilho, canal/provedor/remetente, modo do template, variáveis.

export const CONCEITOS_DISPARO: ConceitoComoUtilizar[] = [
  {
    termo: "Contexto",
    definicao:
      "A entidade que o disparo observa — Proposta, Proposta de leilão, Cobrança CLT, Competência, Boleto e os demais da lista, que vem do catálogo. Define quais campos existem para filtrar, para o contato e para as variáveis da mensagem.",
  },
  {
    termo: "Gatilho",
    definicao:
      "Quando o disparo acontece. Evento reage a um fato do negócio (ex.: proposta paga) e vale para o registro do evento, sem filtro. Rotina roda num horário agendado e exige ao menos um filtro.",
  },
  {
    termo: "Canal, provedor e remetente",
    definicao:
      "Canal é o meio (e-mail, SMS, WhatsApp); provedor é quem entrega (SendGrid, Zenvia, Meta, Vonex); remetente é de qual número/conta sai. As combinações válidas vêm do catálogo — o cadastro só oferece o que o provedor atende.",
  },
  {
    termo: "Escrever a mensagem",
    definicao:
      "O texto é escrito aqui mesmo — é o modo do e-mail e do SMS. Assunto e HTML existem só no e-mail; o SMS manda texto puro.",
  },
  {
    termo: "Template aprovado no provedor",
    definicao:
      "A mensagem já foi aprovada no provedor e aqui só se informa qual é e o que entra em cada posição. É o único modo do WhatsApp, na Meta e na Vonex. A Meta pede o idioma; a Vonex pede o ID do produto e um identificador numérico.",
  },
  {
    termo: "Variável",
    definicao:
      "Um apelido no texto ({{tomadorNome}}) que diz de qual campo do contexto vem o valor. Entra pela barra lateral: com o cursor no texto, clique no campo — o apelido é tirado do nome do campo. Apagar o apelido do texto descarta a variável. Toda variável exige valor: se o campo vier vazio na hora do envio, a mensagem não é enviada para aquele destinatário, em vez de sair com uma lacuna.",
  },
  {
    termo: "Formato da variável",
    definicao:
      "A máscara aplicada ao valor no envio: R$ 1.234,56, 22/09/2026, 09/2026, ***.456.789-**. A variável nasce com o padrão do campo — moeda para valores, data para datas, CPF mascarado para CPF — e dá para trocar clicando no formato, abaixo da mensagem. O slide Formatos mostra os padrões.",
  },
  {
    termo: "Teste do template",
    definicao:
      "O envio da mensagem do disparo só para um destino que você informa, com valores de exemplo nas variáveis. O disparo fica inativo até você confirmar o teste, e o envio de teste não entra no histórico. O slide Teste do template mostra o passo a passo.",
  },
];

// --- Listagem (tela principal) ---

export const PASSOS_LISTAGEM: PassoComoUtilizar[] = [
  {
    titulo: "Evento ou rotina",
    descricao:
      "Disparos por evento reagem na hora a um fato do negócio. Rotina roda no horário agendado, varrendo o contexto.",
  },
  {
    titulo: "Ative ou pause um disparo",
    descricao:
      "Use o switch da coluna Ativo para ligar ou desligar sem precisar excluir. Ativar revalida o cadastro inteiro — se algo ficou incompleto, a ativação é recusada.",
  },
  {
    titulo: "Visualize, edite ou exclua",
    descricao:
      "Os ícones da coluna Ações abrem os detalhes, o formulário de edição, ou pedem confirmação antes de excluir.",
  },
  {
    titulo: "Acompanhe pelos totais",
    descricao:
      "As etiquetas acima da tabela mostram quantos disparos existem, quantos estão ativos e a divisão por tipo de gatilho. Clique em Eventos, Rotinas ou Manuais para ver só aquele tipo; clique de novo para voltar a ver todos.",
  },
  {
    titulo: "A grade é enxuta de propósito",
    descricao:
      "A listagem traz nome, contexto, gatilho e canal. Filtro, mensagem e histórico só aparecem ao abrir o disparo.",
  },
];

// --- Novo disparo ---

export const PASSOS_NOVO_DISPARO: PassoComoUtilizar[] = [
  {
    titulo: "Escolha o contexto primeiro",
    descricao:
      "Tudo depende dele: quais eventos existem, o que dá pra filtrar, de onde sai o contato e quais variáveis a mensagem pode usar. Se trocar o contexto depois, o filtro é zerado e as variáveis saem da mensagem — os campos e os status são de cada contexto. Contato e evento ficam; o que não existir no contexto novo fica marcado em vermelho, pra ser reapontado.",
  },
  {
    titulo: "Defina o gatilho",
    descricao:
      "Evento pede um evento do catálogo compatível com o contexto e não tem filtro. Rotina pede frequência, horário e ao menos um filtro.",
  },
  {
    titulo: "Monte o filtro (só na rotina)",
    descricao:
      "A seção de filtro aparece só no gatilho Rotina. Clique no \"+\" e escolha o campo na barra lateral — a condição aparece já com o campo, pronta pra operador e valor. Enquanto a escolha está pendente o \"+\" fica destacado; clique nele de novo para desistir. Nem todo campo filtra: só os que o catálogo marca como filtráveis aparecem lá.",
  },
  {
    titulo: "Combine com E, OU e grupos",
    descricao:
      "Entre duas condições aparece E ou OU — clique para trocar. O \"( )\" cria um grupo, como um parêntese: dentro dele a combinação é a oposta à de fora, e ele também começa pela escolha do campo da primeira condição.",
  },
  {
    titulo: "Teste a busca",
    descricao:
      "\"Testar busca\" mostra uma amostra do que a rotina encontraria, com os mesmos filtros. Nunca envia mensagem.",
  },
  {
    titulo: "Configure o canal",
    descricao:
      "Um canal por disparo — para avisar pelo mesmo motivo em outro canal, crie outro disparo. Escolha canal, provedor e remetente. O contato é automático: o tomador recebe no e-mail ou no telefone dele; o empregador, no e-mail cadastral ou no telefone do contato do RH. Ao entrar em \"Canal e mensagem\", a identificação do disparo se recolhe e a barra lateral passa a mostrar as variáveis.",
  },
  {
    titulo: "Escreva a mensagem e insira as variáveis",
    descricao:
      "Clique no campo de texto e escolha campos na barra lateral — cada clique insere {{apelido}} no texto, e o campo fica preenchido na lista pra mostrar que já está em uso. Os campos vêm agrupados por entidade (\"Contexto atual\" é o próprio contexto do disparo); clique no nome do grupo pra recolher ou abrir. Abaixo da mensagem, \"De onde vem cada variável\" mostra o campo de origem de cada apelido.",
  },
  {
    titulo: "Teste o template",
    descricao:
      "\"Testar template\" grava o disparo inativo e abre o envio de teste: informe o e-mail, o celular do SMS ou o número do WhatsApp, conforme o canal. A mensagem vai só para esse destino, com valores de exemplo nas variáveis — ninguém do público recebe. Quando ela chegar, Confirmar volta para a lista e ativa o disparo se \"Ativo\" estiver ligado na identificação; Editar template devolve ao formulário. Até confirmar, o disparo não roda para o público.",
  },
];

// --- Editar disparo ---

export const PASSOS_EDITAR_DISPARO: PassoComoUtilizar[] = [
  {
    titulo: "Mesmos campos da criação",
    descricao:
      "Editar usa exatamente o mesmo formulário — altere gatilho, filtro, canal ou mensagem à vontade. A identificação do disparo abre recolhida — clique no título dela para abrir.",
  },
  {
    titulo: "Trocar o contexto limpa o que dependia dele",
    descricao:
      "O contexto define quais registros o filtro alcança e quais campos a mensagem pode usar, então trocar o contexto zera o filtro e tira as variáveis da mensagem — o texto fica, sem os {{campos}} — e desfaz o vínculo com template, que é do contexto anterior. Contato e evento são mantidos; o que não existir no contexto novo fica marcado em vermelho no próprio campo, pra ser reapontado, e o evento que não vale no contexto escolhido fica marcado como fora do contexto.",
  },
  {
    titulo: "Trocar o canal reseta a mensagem",
    descricao:
      "As regras mudam por canal e por provedor (nem todo provedor aceita escrever o texto aqui), então trocar o canal limpa provedor, remetente e mensagem — o contato acompanha o canal.",
  },
  {
    titulo: "Mensagem vinda de um template",
    descricao:
      "Em Origem da mensagem, escolha um template reutilizável do mesmo contexto, canal e provedor. A mensagem segue o template — o que for alterado nele vale para este disparo — e por isso não é editável aqui. Para uma mensagem só deste disparo, escolha \"Escrever a mensagem aqui\" — no WhatsApp, \"Informar o template do provedor aqui\": o conteúdo do template fica como ponto de partida.",
  },
  {
    titulo: "A edição respeita a versão",
    descricao:
      "O cadastro guarda a versão que você abriu. Se outra pessoa salvar antes, o envio é recusado em vez de sobrescrever o trabalho dela.",
  },
  {
    titulo: "Teste depois de mudar a mensagem",
    descricao:
      "\"Testar template\" grava a alteração com o disparo inativo — um disparo ativo fica pausado durante o teste — e manda a mensagem só para o destino que você informar, com valores de exemplo nas variáveis. Confirmar reativa se \"Ativo\" estiver ligado na identificação. Se não ficou como esperado, Editar template devolve ao formulário para ajustar e testar de novo.",
  },
];

// --- Visualizar disparo ---

export const PASSOS_VISUALIZAR_DISPARO: PassoComoUtilizar[] = [
  {
    titulo: "Tela somente leitura",
    descricao:
      "Aqui aparece o cadastro completo — contexto, destinatário, gatilho, filtros e o canal com a mensagem e as variáveis — sem poder alterar.",
  },
  {
    titulo: "Editar a partir daqui",
    descricao: "O botão \"Editar\" no rodapé abre o formulário com os dados já preenchidos.",
  },
];

// --- Templates de mensagem ---
//
// Mesmo glossário do disparo onde o termo é o mesmo (modo da mensagem); os
// conceitos próprios do template são a tripla, o ativo e o vínculo com o disparo.

const conceitoDisparo = (termo: string): ConceitoComoUtilizar =>
  CONCEITOS_DISPARO.find((c) => c.termo === termo) as ConceitoComoUtilizar;

export const CONCEITOS_TEMPLATE: ConceitoComoUtilizar[] = [
  {
    termo: "Template",
    definicao:
      "Um texto de mensagem guardado para ser reaproveitado pelos disparos. Salvar um template não envia nada: ele só fica disponível.",
  },
  {
    termo: "Contexto, canal e provedor",
    definicao:
      "A tripla que amarra o template: um disparo só pode usá-lo se tiver o mesmo contexto, o mesmo canal e o mesmo provedor. O contexto também decide quais campos aparecem como variável.",
  },
  {
    termo: "Ativo",
    definicao:
      "Template inativo continua cadastrado, mas não pode ser escolhido por um disparo — e os disparos que já o usam falham no envio enquanto ele estiver inativo.",
  },
  {
    termo: "Vínculo com o disparo",
    definicao:
      "O disparo que usa o template segue o template: editar o template muda a mensagem desses disparos, inclusive dos ativos, a partir do próximo envio. Desativar o template faz esses disparos falharem no envio e os impede de ser salvos ou ativados. Template em uso não pode ser excluído.",
  },
  conceitoDisparo("Escrever a mensagem"),
  conceitoDisparo("Template aprovado no provedor"),
  conceitoDisparo("Variável"),
  conceitoDisparo("Formato da variável"),
];

export const PASSOS_LISTAGEM_TEMPLATES: PassoComoUtilizar[] = [
  {
    titulo: "Filtre a lista",
    descricao:
      "Recorte por situação (ativos ou inativos), por contexto e por canal. Os filtros se combinam; o total acima da tabela conta o que está na tela.",
  },
  {
    titulo: "Ative ou pause um template",
    descricao:
      "Use o switch da coluna Ativo. Desligar pede confirmação, porque os disparos que usam o template falham no envio enquanto ele estiver inativo. Ao ligar ou desligar, o template inteiro é revalidado — se o conteúdo ficou incompatível com o catálogo, a troca é recusada.",
  },
  {
    titulo: "Edite ou exclua",
    descricao:
      "Os ícones da coluna Ações abrem o formulário de edição ou pedem confirmação antes de excluir. Editar muda a mensagem dos disparos que usam o template; template em uso por algum disparo não pode ser excluído.",
  },
  {
    titulo: "Volte para os disparos",
    descricao: "A seta no cabeçalho leva de volta à lista de disparos.",
  },
];

export const PASSOS_NOVO_TEMPLATE: PassoComoUtilizar[] = [
  {
    titulo: "Declare a tripla primeiro",
    descricao:
      "Contexto, canal e provedor definem quais disparos poderão usar o template e quais campos aparecem na barra lateral. O provedor só oferece quem atende o canal.",
  },
  {
    titulo: "Escreva a mensagem",
    descricao:
      "No e-mail há assunto e formato (texto ou HTML); no SMS, um contador mostra o limite de caracteres. No WhatsApp, informe o template aprovado no provedor — identificador, idioma (Meta) ou ID do produto (Vonex) — e o que entra em cada parâmetro.",
  },
  {
    titulo: "Insira as variáveis pela barra lateral",
    descricao:
      "Clique no texto, ou num parâmetro, e depois no campo desejado. Variável digitada à mão, sem passar pela barra lateral, fica apontada como não mapeada.",
  },
  {
    titulo: "Remetente e contato ficam no disparo",
    descricao:
      "O template guarda só o texto. De qual número a mensagem sai é escolhido em cada disparo, e o contato de quem recebe sai do destinatário e do canal do disparo.",
  },
];

export const PASSOS_EDITAR_TEMPLATE: PassoComoUtilizar[] = [
  {
    titulo: "Editar muda os disparos vinculados",
    descricao:
      "Os disparos que usam este template seguem o template: ao salvar, a mensagem deles muda também, inclusive a dos ativos, a partir do próximo envio — por isso o Salvar pede confirmação. Desativar faz esses disparos falharem no envio; excluir é recusado enquanto algum disparo usar o template.",
  },
  {
    titulo: "Trocar contexto, canal ou provedor",
    descricao:
      "Muda quais disparos podem usar o template. Trocar o canal limpa provedor e mensagem; trocar o contexto tira as variáveis da mensagem — o texto fica, sem os {{campos}}. Com o template em uso, os disparos vinculados deixam de bater com a tripla nova e falham no envio — nesse caso, prefira criar um template novo.",
  },
  {
    titulo: "Sair sem salvar pede confirmação",
    descricao:
      "Voltar ou cancelar com alteração não salva pergunta antes de descartar o que foi preenchido.",
  },
];
